import { Injectable } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { NavigationSkipped, NavigationStart, Router } from '@angular/router';
import { Location } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';

import { RouteHelper, RoutePaths } from '@helper/route-paths';
import { Skus } from '@helper/skus';
import { Product, Product_SkuColor } from '@proto/store/api/store/product';
import { CartDialogModel } from '../ui/cart-dialog/cart-dialog-model';
import { CartDialogComponent } from '../ui/cart-dialog/cart-dialog.component';
import { InfoType, ProductInfoComponent } from '../ui/product/mobile/product-info/product-info.component';
import { ScrollPositionRestorerService } from '@service/scroll-position-restorer.service';
import { CartDialogGuideComponent } from '../ui/guide/cart-dialog-guide/cart-dialog-guide.component';
import { PreferenceKey } from '@helper/preference-key';

import { filter, of, Subject, switchMap, take, takeUntil } from 'rxjs';

export class ShoppingCartData {
  constructor(
    public skuId: string,
    public amount: number, // 預設開啟的數量, 購物車用
    public destroyOnClose: boolean, // 關閉後使否清除狀態及資料
    public product: Product | undefined,
    public showDetail: boolean, // 顯示商品詳情
    public replace: boolean // 購物車更新
  ) {}
}

@Injectable({
  providedIn: 'root',
})
export class CartBottomSheetService {
  private readonly lifecycleEmitter = new Subject<void>();
  private readonly restoreDelay = 100;
  private readonly purchaseBtnHeight = 40;
  private readonly heightDeduction = 90;

  constructor(
    public matBottomSheet: MatBottomSheet,
    private viewModel: CartDialogModel,
    private router: Router,
    private location: Location,
    private scrollService: ScrollPositionRestorerService,
    private dialog: MatDialog
  ) {
    this.router.events
      .pipe(filter(e => e instanceof NavigationStart || e instanceof NavigationSkipped))
      .subscribe(e => {
        if (e instanceof NavigationStart) {
          this.matBottomSheet._openedBottomSheetRef
            ?.afterDismissed()
            .pipe(takeUntil(this.lifecycleEmitter))
            .subscribe(() => {
              this.viewModel.cleanUpViewModel();
              this.lifecycleEmitter.next();
            });
        }
        this.onClose();
      });
  }

  openCart(data: ShoppingCartData): void {
    const originScrollY = window.scrollY;
    const cartDialogRef = CartDialogComponent.open(
      this.matBottomSheet,
      data.skuId,
      data.amount,
      data.destroyOnClose,
      this.viewModel.detectService.isTablet(),
      data.product,
      data.showDetail,
      data.replace
    );
    try {
      if (localStorage && !localStorage.getItem(PreferenceKey.GUIDE_CART_DIALOG)) {
        cartDialogRef
          .afterOpened()
          .pipe(takeUntil(this.lifecycleEmitter))
          .subscribe(() => {
            setTimeout(() => {
              if (cartDialogRef.instance) {
                CartDialogGuideComponent.open(
                  this.dialog,
                  cartDialogRef.instance.contentHeight() + this.purchaseBtnHeight - this.heightDeduction
                );
              }
            }, 0);
          });
      }
    } catch {
      // ignore
    }
    cartDialogRef
      .backdropClick()
      .pipe(
        switchMap(() =>
          this.matBottomSheet._openedBottomSheetRef
            ? this.matBottomSheet._openedBottomSheetRef?.afterDismissed()
            : of(undefined)
        ),
        takeUntil(this.lifecycleEmitter)
      )
      .subscribe(() => {
        if (originScrollY !== window.scrollY) {
          // 因套件問題，捲軸位置會因為開啟後跑掉，導致關閉後，沒辦法回去原本位置.
          window.scrollTo({ top: originScrollY });
        }
        this.viewModel.updateSku();
        if (this.viewModel.currentState.destroyOnClose) {
          this.viewModel.cleanUpViewModel();
          this.lifecycleEmitter.next();
        }
      });
  }

  openGallery(color: Product_SkuColor): void {
    const skus = Skus.fromId(this.viewModel.currentState.product?.meta?.productId || this.viewModel.currentState.skuId);
    this.viewModel.toGallery.set(true);
    this.matBottomSheet._openedBottomSheetRef
      ?.afterDismissed()
      .pipe(takeUntil(this.lifecycleEmitter))
      .subscribe(() => {
        this.router.navigate([RouteHelper.skuGallery(skus.productId, color.id)]);
        this.lifecycleEmitter.next();
      });
    this.matBottomSheet._openedBottomSheetRef?.dismiss();
  }

  openFitting(): void {
    this.matBottomSheet._openedBottomSheetRef
      ?.afterDismissed()
      .pipe(take(1), takeUntil(this.lifecycleEmitter))
      .subscribe(() => {
        const product = this.viewModel.apiResult();
        ProductInfoComponent.open(
          this.matBottomSheet,
          product.fittingReportImage ? InfoType.FITTING_TABLE : InfoType.SIZE_TABLE,
          product,
          this.viewModel.detectService.isTablet()
        )
          .afterDismissed()
          .pipe(take(1), takeUntil(this.lifecycleEmitter))
          .subscribe(() => this.reOpenCart());
      });
    this.matBottomSheet._openedBottomSheetRef?.dismiss();
  }

  onGalleryClose(index: number, needReopen: boolean): void {
    if (!this.location.path().includes(RoutePaths.GALLERY)) {
      return;
    }
    const color = this.viewModel.apiResult().colors[index];
    if (color) {
      this.viewModel.setColor(color);
    }
    if (needReopen) {
      this.scrollService
        .waitDomHeightReady()
        .pipe(take(1))
        .subscribe(top => {
          window.scrollTo({ top: top });
          setTimeout(() => this.reOpenCart(), this.restoreDelay);
        });
    }
    this.location.back();
  }

  onClose(): void {
    if (this.viewModel.currentState.destroyOnClose) {
      this.matBottomSheet._openedBottomSheetRef
        ?.afterDismissed()
        .pipe(takeUntil(this.lifecycleEmitter))
        .subscribe(() => {
          this.viewModel.cleanUpViewModel();
          this.lifecycleEmitter.next();
        });
    }
    this.matBottomSheet.dismiss(undefined);
    this.viewModel.updateSku();
  }

  routeTo(isDetail: boolean): void {
    this.matBottomSheet._openedBottomSheetRef
      ?.afterDismissed()
      .pipe(takeUntil(this.lifecycleEmitter))
      .subscribe(() => {
        const route = isDetail ? this.viewModel.detailUrl() : this.viewModel.saleEventUrl();
        if (route) {
          setTimeout(
            () => this.router.navigate([route], isDetail ? { queryParams: this.viewModel.params() } : undefined),
            0
          );
        }
        this.viewModel.cleanUpViewModel();
        this.lifecycleEmitter.next();
      });
    this.matBottomSheet.dismiss(undefined);
  }

  reOpenCart(): void {
    if (!this.viewModel.apiResult()) {
      return;
    }
    this.viewModel.currentState.amount = this.viewModel.count() ?? 0;
    this.viewModel.currentState.product = this.viewModel.apiResult();
    this.openCart(this.viewModel.currentState);
  }
}
